import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container, Grid, Paper, Typography, MenuItem, Box, Select, InputLabel, FormControl
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useUser } from '../UserContext';
import '../Relatorio/index.css';

// ✅ Primeiro: Campos de filtro declarados no topo
const camposFiltro = {
  semana: 'Semana',
  ano: 'Ano',
  dpesDtaCadastro: 'Data Contagem',
  dpcDescricao: 'Frigorífico',
  proDescricao: 'Produto',
  contagem: 'Item Contado?',
  subDescricao: 'Tipo',
  recClassificacao: 'Classificação',
};

// ✅ Depois: Função gerarOpcoesFiltro com useCallback
const Relatorio = () => {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const [dadosOriginais, setDadosOriginais] = useState([]);
  const [filtros, setFiltros] = useState({});
  const [dadosFiltrados, setDadosFiltrados] = useState([]);
  const [opcoesFiltro, setOpcoesFiltro] = useState({});

  const gerarOpcoesFiltro = useCallback((dados) => {
  const opcoes = {};
  Object.keys(camposFiltro).forEach(campo => {
    const valoresUnicos = [...new Set(dados.map(item => item[campo] || ''))];
    opcoes[campo] = valoresUnicos;
  });
  setOpcoesFiltro(opcoes);
}, []); // ✅ array vazio, camposFiltro está fora do hook

  useEffect(() => {
    if (!userInfo?.username || !userInfo?.company) {
      localStorage.removeItem('userInfo');
      navigate('/');
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    const buscarDados = async () => {
      try {
        const response = await fetch(`${userInfo.url}/departamentoestoq/relatorio`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'CNPJ_CPF': userInfo.alias
          }
        });
        const data = await response.json();
        setDadosOriginais(data);
        setDadosFiltrados(data);
        gerarOpcoesFiltro(data);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };

    buscarDados();
  }, [userInfo, gerarOpcoesFiltro]);

  const handleFiltro = (campo, valor) => {
    const novosFiltros = { ...filtros, [campo]: valor };
    setFiltros(novosFiltros);
    filtrarDados(dadosOriginais, novosFiltros);
  };

  const filtrarDados = (dados, filtrosAtuais) => {
    const resultado = dados.filter(item => {
      return Object.entries(filtrosAtuais).every(([key, value]) => {
        if (!value) return true;
        return item[key]?.toString() === value.toString();
      });
    });
    setDadosFiltrados(resultado);
  };

  const agruparTabela = () => {
    const mapa = new Map();
    dadosFiltrados.forEach(item => {
      const chave = `${item.dpcDescricao}|${item.proDescricao}|${item.contagem}|${item.subDescricao}|${item.recClassificacao}`;
      if (!mapa.has(chave)) {
        mapa.set(chave, {
          dpcDescricao: item.dpcDescricao,
          proDescricao: item.proDescricao,
          contagem: item.contagem,
          subDescricao: item.subDescricao,
          recClassificacao: item.recClassificacao,
          dpesQtdProduto: 0
        });
      }
      mapa.get(chave).dpesQtdProduto += item.dpesQtdProduto;
    });
    return Array.from(mapa.values());
  };

  const dadosGrafico = dadosFiltrados.reduce((acc, curr) => {
  const key = curr.dpcDescricao || 'Sem Frigorífico';
  const qtd = Number(curr.dpesQtdProduto) || 0;
  acc[key] = (acc[key] || 0) + qtd;
  return acc;
  }, {});

  const dadosGraficoArray = Object.entries(dadosGrafico).map(([descricao, qtd]) => ({
    dpcDescricao: descricao,
    qtd
  }));

  return (
    <>
      <header className="header">
        <h2>Bem-vindo, {userInfo.username}!</h2>
        <h1>Relatório de Estoque</h1>
      </header>

      <Link to="/menu" className="link-style">Voltar ao menu</Link>

      <Container sx={{ marginTop: 4 }}>
        <Typography variant="h4" gutterBottom>Filtros</Typography>
        <Paper className="filtros-container" sx={{ padding: 2, marginTop: 3 }}>
          <Grid container spacing={2}>
            {Object.entries(camposFiltro).map(([campo, label]) => (
              <Grid item xs={12} sm={6} md={4} key={campo} className="filtro-item">
                <FormControl fullWidth size="small">
                  <InputLabel>{label}</InputLabel>
                  <Select
                    value={filtros[campo] || ''}
                    label={label}
                    onChange={(e) => handleFiltro(campo, e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {opcoesFiltro[campo]?.map((valor, idx) => (
                      <MenuItem key={idx} value={valor}>
                        {campo === 'dpesDtaCadastro'
                          ? new Date(valor).toLocaleDateString('pt-BR')
                          : valor}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper sx={{ marginTop: 4, padding: 2 }}>
        <Typography variant="h6">Resultado Agrupado</Typography>
        <Box className="tabela-wrapper">
          <table>
            <thead>
              <tr>
                <th>Frigorífico</th>
                <th>Produto</th>
                <th>Quantidade Total</th>
                <th>Item Contado?</th>
                <th>Tipo</th>
                <th>Classificação</th>
              </tr>
            </thead>
            <tbody>
              {agruparTabela().map((item, idx) => (
                <tr key={idx}>
                  <td>{item.dpcDescricao}</td>
                  <td>{item.proDescricao}</td>
                  <td>{item.dpesQtdProduto}</td>
                  <td>{item.contagem}</td>
                  <td>{item.subDescricao}</td>
                  <td>{item.recClassificacao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        </Paper>

        <Paper className="grafico-container">
          <Typography variant="h6">Gráfico: Quantidade por Frigorífico</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={dadosGraficoArray}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dpcDescricao" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="qtd" fill="#1976d2" name="Qtd Total" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Container>
    </>
  );
};

export default Relatorio;